/**
 * Shared **Visual | Expression** condition editor — the one core reused by BOTH
 * the page builder (`visible_when`, `{{ }}` templates) and the workflow editor
 * (`filter`/`if` predicates, RAW JS). Visual drives the `ConditionBuilder` and
 * compiles to ONE expression; Expression is whatever raw editor the caller
 * passes via `renderExpression` (page `ExpressionField` or workflow Monaco).
 *
 * The tree is LOCAL state, decoupled from the lossy compile→parse round-trip so
 * an in-progress / empty-field row persists; re-seeded only when `value` changes
 * from OUTSIDE (node switch / raw edit), tracked via `lastEmitted`. When the
 * stored value isn't representable as a simple rule list, Visual is unavailable.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  compileCondition,
  emptyCondition,
  parseCondition,
  type ValueType,
} from "@/lib/conditionExpr";
import { cn } from "@/lib/cn";

import { ConditionBuilder } from "./ConditionBuilder";
import { Field } from "./inspectorKit";

export function ConditionEditor({
  value,
  onChange,
  fields = [],
  fieldTypes = {},
  braces = true,
  renderExpression,
  label,
  hint,
}: {
  value: string;
  onChange: (expr: string) => void;
  /** Binding options for the field dropdown. */
  fields?: string[];
  /** Binding → value type, for pre-filling the type chip (schema-derived). */
  fieldTypes?: Record<string, ValueType>;
  /** `true` → `{{ }}` template (pages); `false` → raw JS (workflow predicates). */
  braces?: boolean;
  /** The raw-editor for Expression mode. */
  renderExpression: (value: string, onChange: (v: string) => void) => ReactNode;
  label?: string;
  hint?: string;
}) {
  const visualAvailable = parseCondition(value, braces) !== null;
  const [mode, setMode] = useState<"visual" | "expr">(visualAvailable ? "visual" : "expr");
  const [tree, setTree] = useState(() => parseCondition(value, braces) ?? emptyCondition());
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setTree(parseCondition(value, braces) ?? emptyCondition());
      lastEmitted.current = value;
    }
  }, [value, braces]);
  const onTreeChange = (next: typeof tree) => {
    setTree(next);
    const compiled = compileCondition(next, braces);
    lastEmitted.current = compiled;
    onChange(compiled);
  };
  useEffect(() => {
    if (!visualAvailable && mode === "visual") setMode("expr");
  }, [visualAvailable, mode]);

  const Tab = ({ id, label: lbl, disabled }: { id: "visual" | "expr"; label: string; disabled?: boolean }) => (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={mode === id}
      onClick={() => setMode(id)}
      title={disabled ? "Expression too complex for the visual builder" : undefined}
      className={cn(
        "flex-1 rounded px-2 py-0.5 font-medium transition-colors disabled:opacity-40",
        mode === id ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {lbl}
    </button>
  );

  const inner = (
    <>
      <div className="mb-2 flex rounded-md border border-border p-0.5 text-[11px]">
        <Tab id="visual" label="Visual" disabled={!visualAvailable} />
        <Tab id="expr" label="Expression" />
      </div>
      {mode === "visual" && visualAvailable ? (
        <ConditionBuilder
          value={tree}
          onChange={onTreeChange}
          fields={fields}
          fieldTypes={fieldTypes}
        />
      ) : (
        renderExpression(value, onChange)
      )}
    </>
  );

  return label ? (
    <Field label={label} layout="stack" hint={hint}>
      {inner}
    </Field>
  ) : (
    <div className="flex flex-col">{inner}</div>
  );
}
